import { toTitleCase } from "./helpers";

/**
 * Given and array of forecast objects, return the forecast for the given zone
 * @param {array} fullForecast 
 * @param {integer} zoneId 
 * @returns forecast object
 */
export const getZoneForecast = (fullForecast, zoneId) => {
    return fullForecast.find((f) => f.code.startsWith(zoneId));
};

/**
 * @param {string} alert
 * @returns {string} severity level: watch || advisory || warning
 */
const getAlertSeverity = (alert) => {
    if (alert.toLowerCase().includes('watch')) return 'watch';
    if (alert.toLowerCase().includes('advisory')) return 'advisory';
    if (alert.toLowerCase().includes('warning')) return 'warning';
};

/**
 * Format an alert string. 
 * Everything before and including the keyword "watch || advisory || warning" is considered the alert text.
 * Everything after the keyword is considered the alert timeframe.
 * 
 * @param {string} alert
 * @returns {object} containing the severity, alertText, and timeframe
 */
export const formatAlert = (alert) => {
    const severity = getAlertSeverity(alert);
    const index = alert.toLowerCase().indexOf(severity);

    const alertText = toTitleCase(alert.slice(0, index + severity.length));
    const timeframe = toTitleCase(alert.slice(index + severity.length));

    return {
        severity,
        alertText,
        timeframe
    }
};

const findRegionByFeatureId = (regions, id) => {
    if (!regions) return null;

    for (const region of regions) {
        for (const feature of region.features) {
            if (id.startsWith(feature.properties.ID)) {
                return region;
            }
        }
    }
    return null;
}

/**
 * From an array of forecasts, group unique alerts by region and zone
 * @param {array} forecasts 
 * @param {array} regionData 
 * @returns {array} of alerts grouped by region and zone
 */
export const groupAlerts = (forecasts, regionData) => {
    if (!forecasts || !regionData ) return [];

    const groupedAlertsMap = new Map();

    forecasts.forEach(forecast => {
        const alerts = forecast?.alerts;
        if (!alerts) return;

        const region = findRegionByFeatureId(regionData, forecast.code);

        if (!region) {
            console.warn(`No region found for forecast code: ${forecast.code}`);
            return;
        }

        for (const alertType in alerts) {
            if (alerts.hasOwnProperty(alertType)) {
                const alertArray = alerts[alertType];
                
                alertArray.forEach(alert => {
                    const alertText = alert.alertText;
    
                    if (!groupedAlertsMap.has(alertText)) {
                        groupedAlertsMap.set(alertText, {
                            alertText,
                            alertType,
                            issuanceTime: forecast.issuanceTime,
                            regions: new Map()
                        });
                    }
    
                    const alertEntry = groupedAlertsMap.get(alertText);

                    // if forecast.issuanceTime is more recent than the current alertEntry issuanceTime, update it
                    // to reflect "last updated"
                    if (new Date(forecast.issuanceTime) > new Date(alertEntry.issuanceTime)) {
                        alertEntry.issuanceTime = forecast.issuanceTime;
                    }
    
                    if (!alertEntry.regions.has(region.id)) {
                        alertEntry.regions.set(region.id, []);
                    }
    
                    const regionEntry = alertEntry.regions.get(region.id);
    
                    regionEntry.push({
                        zone: forecast.location,
                        timeframe: alert.timeframe,
                    });
                });
            }
        }
    });

    // Transform the Map to an array
    const alertsArray = Array.from(groupedAlertsMap.entries()).map(([alertText, alertEntry]) => ({
        alertText: alertEntry.alertText,
        alertType: alertEntry.alertType,
        issuanceTime: alertEntry.issuanceTime,
        regions: Array.from(alertEntry.regions.entries()).map(([region, zones]) => ({
            region,
            zones
        }))
    }));

    return alertsArray;
};

/**
 * Parsing of forecast data from api.weather.gov below is largely based on
 * https://github.com/axiom-data-science/nws-marine-forecast-api 
 * and ported to JavaScript with the help of ChatGPT
 */

const getSynopsis = (productText) => {
    const SYNOPSIS_PATTERN = /synopsis([\s\S]*?)\$\$/i;

    // Get the first substring that matches the synopsis pattern
    const synopsisMatch = productText.match(SYNOPSIS_PATTERN);

    let match = "";
    if (synopsisMatch) {
        let synopsis = synopsisMatch[1];

        // Sometimes the synopsis doesn't begin/end with `...` so we should assume it ends
        // with the first line break if we do not see bounding `...`
        if (synopsis.split("...").length > 1) {
            match = synopsis.split("...").slice(1).join(" ");
        } else if (synopsis.split("\n").length > 1) {
            match = synopsis.split("\n").slice(1).join(" ");
        }
    }

    // Remove any newlines and extra spaces
    match = match.replace(/\n/g, " ").trim().replace(/\s+/g, " ");

    return match;
};

export const parseForecast = (upstreamForecast) => {
    const forecastChunks = upstreamForecast['productText'].split('$$');
    
    // remove preamble section from start of forecast, removing blank lines
    const preamble = forecastChunks
        .shift()
        .split('\n')
        .filter(line => line.trim() && line.trim() !== '000')
        .map(line => line.trim());

    // Extract the synopsis from the product text
    const synopsis = getSynopsis(upstreamForecast['productText']);

    const forecasts = [];
    const unprocessedChunks = [];

    forecastChunks.forEach((forecastChunk) => {
        const forecast = {};
        const lines = forecastChunk.split('\n').map(line => line.trim()).filter(line => line);

        if (!lines.length) {
          return null;
        }

        // include the raw text forecast
        forecast["raw"] = lines.join('\n');

        const code = [];
        while (lines.length) {
            const line = lines.shift();
            code.push(line.replace(/-+$/, '').trim());
            if (line.endsWith('-')) {
                break;
            }
        }
        forecast['code'] = code.join(' ');
        if (!lines.length) {
            unprocessedChunks.push(forecastChunk);
            return null;
        }

        const location = [];
        while (lines.length) {
            const line = lines.shift();
            location.push(line.replace(/-+$/, '').trim());
            if (line.endsWith('-') && /^\d{3}/.test(lines[0])) {
                break;
            }
        }
        forecast['location'] = location.join(' ');
        if (!lines.length) {
            unprocessedChunks.push(forecastChunk);
            return null;
        }

        const forecastDateStr = lines.shift().trim();
        forecast['forecastDateOriginal'] = forecastDateStr;

        // times are sometimes duplicated in multiple time zones
        // we only need one
        const timeSepIdx = forecastDateStr.indexOf(' ') - 2;
        const formattedForecastDateStr = `${forecastDateStr.slice(0, timeSepIdx)}:${forecastDateStr.slice(timeSepIdx)}`;
        
        // TODO: parse date as needed...
        
        // const forecastDate = dateparser.parse(formattedForecastDateStr);
        // if (forecastDate) {
        //     forecast['forecastDate_local'] = forecastDate;
        //     forecast['forecastDate'] = forecastDate.toISOString();
        // }

        forecast['forecastDate'] = formattedForecastDateStr

        if (lines[0].trim() === 'UPDATED') {
            lines.shift();
            forecast['isUpdated'] = true;
        } else {
            forecast['isUpdated'] = false;
        }

        const advisories = [];
        const subforecasts = {};
        const unprocessed = [];
        const alerts = {
            watches: [],
            advisories: [], 
            warnings: [],
        };
        // let inAdvisory = false;
        // let advisory = [];
        let inSubforecast = null;

          lines.forEach((line) => {
            if (line.startsWith('...')) {
                const alert = line.replaceAll('...', '').trim();

                advisories.push(alert);

                const formattedAlert = formatAlert(alert);
                if (formattedAlert.severity === "watch") {
                    alerts.watches.push(formattedAlert);
                } else if (formattedAlert.severity === "advisory") {
                    alerts.advisories.push(formattedAlert);
                } else if (formattedAlert.severity === "warning") {
                    alerts.warnings.push(formattedAlert);
                }                
            }

            // if (inAdvisory) {
            //     advisory.push(line.replaceAll('...', '').trim());
            //     if (line.endsWith('...')) {
            //         inAdvisory = false;
            //         advisories.push(advisory.join(' '));
            //         advisory = [];
            //     }
            //     return null;
            // }

            else if (line.startsWith('.')) {
                const subforecastStartLine = line.slice(1).split('...');
                if (subforecastStartLine.length === 2) {
                    inSubforecast = subforecastStartLine[0];
                    subforecasts[inSubforecast] = [];
                    line = subforecastStartLine[1];
                }
                if (inSubforecast) {
                    subforecasts[inSubforecast].push(line.trim());
                } else {
                    unprocessed.push(line);
                }
            }
            
        });

        // TODO: deprecate dumping everything in advisories
        // this could also be organized better, lots of artifacts of the Python implementation ported somewhat line for line to JavaScript
        forecast['advisories'] = advisories;
        forecast['alerts'] = alerts;
        forecast['subForecasts'] = Object.entries(subforecasts).map(([key, value]) => ({
            'timeframe': key,
            'forecastText': value.join(' ')
        }));
        forecast['unprocessed'] = unprocessed;
        forecast['issuanceTime'] = upstreamForecast['issuanceTime'];
        forecasts.push(forecast);
    });

    const regionForecast = {
        'preamble': preamble.join('\n'),
        'shortSynopsis': synopsis,
        'forecasts': forecasts,
        'unprocessed': unprocessedChunks
    };

    return regionForecast;
};

/**
* Filters for regions that have specific alerts. Pass the type of alerts that you want to be returned
*
* @param {Array} forecastRegions - Array of regions and their forecasts.
* @param {String} severity - Type of alert to filter for (advisory, warning, or watch). Optional - if omitted, regions with no alerts will be returned.
* @returns {Array} - Array of region names that have a given alert type.
*/
export const filterRegionsByAlert = (forecastRegions, severity = null) => {
    // map singular terminology to plural
    const severityMapping = {
        warning: "warnings",
        advisory: "advisories",
        watch: "watches",
    }; 

    const severityKey = severityMapping[severity];

    if (!severity) {
        return forecastRegions.filter((region) => 
            region.alerts.watches.length < 1 
            && region.alerts.advisories.length < 1
            && region.alerts.warnings.length < 1
        ).map((region) => region.code);
    }

    const filteredRegions = forecastRegions.filter((region) => region.alerts[severityKey]?.length > 0);
    return filteredRegions.map((region) => region.code);
};

/**
 *
 * @param {array} featureCollection - array of geoJson FeatureCollection objects
 * @param {array} regionIds - array of region IDs to filter by
 */
export const filterFeatureCollectionsById = (featureCollections, regionIds) => {
    return featureCollections.map((featureCollection) => {
        const filteredFeatures = featureCollection.features.filter((feature) => 
            regionIds.some((id) => id.startsWith(feature.properties.ID))
        );
        return {
            type: 'FeatureCollection',
            features: filteredFeatures
        };
    });
};

